/**
 * Shapes of the advertising globals the vendor bootstraps put on `window`.
 *
 * Each vendor ships a small queueing stub that buffers calls made before its
 * library arrives; the library then drains the queue and takes over. The stubs
 * are declared here, rather than each component casting `window` to its own
 * ad-hoc shape, so there is one contract per global.
 */

/** Meta. `fbevents.js` drains `queue` and then routes calls via `callMethod`. */
export interface FbqStub {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[][];
    loaded: boolean;
    version: string;
    push: FbqStub;
}

/** Pinterest. `core.js` drains `queue`. */
export interface PintrkStub {
    (...args: unknown[]): void;
    queue: unknown[][];
    version: string;
}

/** LinkedIn. `insight.min.js` drains `q`, whose entries are `[action, data]`. */
export interface LintrkStub {
    (action: string, data?: unknown): void;
    q: unknown[][];
}

/** Outbrain. `obtp.js` drains `queue` and then routes calls via `dispatch`. */
export interface ObApiStub {
    (...args: unknown[]): void;
    dispatch?: (...args: unknown[]) => void;
    queue: unknown[][];
    version: string;
    loaded: boolean;
    marketerId: string | string[];
}

/** Key holding the boot record described on `claimBoot`. */
export const BOOT_RECORD_KEY = '__apAdvertisingBooted';

/**
 * Gate the advertising trackers wait on before touching a vendor global.
 * `release` is called once the outcome is known, whether the pixels booted or
 * consent suppressed them.
 */
interface AdvertisingGate {
    promise: Promise<void>;
    release: () => void;
}

export type AdvertisingWindow = Window &
    typeof globalThis & {
        fbq?: FbqStub;
        _fbq?: FbqStub;
        pintrk?: PintrkStub;
        lintrk?: LintrkStub;
        obApi?: ObApiStub;
        _linkedin_partner_id?: string;
        _linkedin_data_partner_ids?: string[];
        __apAdvertisingReady?: AdvertisingGate;
        [BOOT_RECORD_KEY]?: Set<string>;
    };

export const getAdvertisingWindow = (): AdvertisingWindow =>
    window as AdvertisingWindow;

/**
 * Open the gate the Meta, Pinterest and Outbrain trackers await, so events
 * raised before the pixels boot are held rather than dropped.
 *
 * The gate is created at import time because the timing is tight and fixed: the
 * consent check costs `ClientAdvertisingPixels` a state update, so the pixels
 * mount on the second commit, while the tracker is created in `RootPage` and
 * flushes its queued first `PageView` at the end of the first. A gate created
 * inside a pixel's own effect would be built after the event it needs to hold.
 */
export const openAdvertisingGate = (w: AdvertisingWindow): void => {
    if (w.__apAdvertisingReady) return;

    let release = (): void => {};
    const promise = new Promise<void>((resolve) => {
        release = resolve;
    });
    w.__apAdvertisingReady = { promise, release };
};

/**
 * Let advertising events through. Called once every pixel has had its chance to
 * boot, and on the consent-denied path so suppressed sessions do not wait on
 * pixels that will never arrive.
 */
export const releaseAdvertisingGate = (w: AdvertisingWindow): void => {
    w.__apAdvertisingReady?.release();
};

if (typeof window !== 'undefined') {
    openAdvertisingGate(getAdvertisingWindow());
}

/**
 * Claim a one-off registration, returning `true` only the first time `key` is
 * seen in this document. Callers use it to keep effect re-runs from repeating a
 * vendor registration that is only meaningful once.
 *
 * The record lives on `window` rather than in module scope because these
 * modules are part of the server bundle, where module state would be shared by
 * every request the process serves. A document-scoped record is also the
 * accurate lifetime: it survives hot reloads and resets naturally between tests.
 *
 * Vendors that expose their own registry — LinkedIn's
 * `_linkedin_data_partner_ids`, Outbrain's `obApi.marketerId` — are read
 * directly instead, since that state is owned by the vendor and also sees
 * registrations made by GTM or the Business Manager template.
 */
export const claimBoot = (w: AdvertisingWindow, key: string): boolean => {
    const booted = (w[BOOT_RECORD_KEY] ??= new Set<string>());
    if (booted.has(key)) return false;
    booted.add(key);
    return true;
};
